import React from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";
import { Memorial } from "@models/Memorial";
import { Media } from "@models/Media";
import { TimelineEntry } from "@models/Timeline";
import { formatDate, formatTime } from "@util/string";
import { withConditionalCacheBust } from "@util/cache-bust";

Font.register({
    family: "YesevaOne",
    src: "/assets/fonts/YesevaOne-Regular.ttf",
});

Font.registerEmojiSource({
    format: "png",
    url: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/",
});

export interface MemorialDocumentProps {
    memorial: Memorial;
    businessLogo: string;
    videoPosters: Record<string, string>; // video src -> captured frame data URL
}

const FIOH_LOGO = "/assets/images/fioh-logo.png";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const remoteImage = (src: string) => ({ uri: withConditionalCacheBust(src) });

// Images render directly; videos render their captured preview frame, or nothing if none
const mediaImage = (media: Media | undefined, videoPosters: Record<string, string>) => {
    if (!media) return null;
    if (media.type === "image") return remoteImage(media.src);
    return videoPosters[media.src] || null;
};

const formatTimelineDate = (entry: TimelineEntry) => [
    entry.day,
    entry.month ? MONTHS[entry.month - 1] : undefined,
    entry.year,
].filter(Boolean).join(" ");

const sortTimeline = (timeline: TimelineEntry[]) => timeline
    .slice()
    .sort((a, b) => (a.year - b.year)
        || ((a.month || 0) - (b.month || 0))
        || ((a.day || 0) - (b.day || 0)));

const MemorialDocument: React.FC<MemorialDocumentProps> = ({
    memorial, businessLogo, videoPosters,
}) => {
    const fullName = `${memorial.firstName} ${memorial.lastName}`;
    const tributes = memorial.tributes.filter((t) => !t.forReview);
    const timeline = sortTimeline(memorial.timeline);
    const gallery = memorial.gallery
        .map((section) => ({
            ...section,
            images: section.media
                .map((m) => ({ id: m._id, src: mediaImage(m, videoPosters) }))
                .filter((m) => m.src),
        }))
        .filter((section) => section.images.length > 0);

    const serviceDetails = [
        memorial.serviceDateTime && {
            label: "Date",
            value: `${formatDate(memorial.serviceDateTime, "long", "long")} at ${formatTime(memorial.serviceDateTime)}`,
        },
        memorial.serviceLocation && { label: "Location", value: memorial.serviceLocation },
        memorial.serviceInformation && { label: "Information", value: memorial.serviceInformation },
        memorial.liveStreamLink && { label: "Live Stream", value: memorial.liveStreamLink },
        memorial.graveLocation && { label: "Grave Location", value: memorial.graveLocation },
        memorial.plotInfo && { label: "Plot", value: memorial.plotInfo },
    ].filter(Boolean) as { label: string; value: string }[];

    const hasContent = serviceDetails.length > 0 || memorial.obituary
        || timeline.length > 0 || gallery.length > 0 || tributes.length > 0;

    return (
        <Document title={`${fullName} - Memorial`}>
            {/* Cover Page - matches the Guest Book cover */}
            <Page size="A4" style={styles.page}>
                <View style={styles.cover}>
                    <View style={styles.coverHeader}>
                        {businessLogo && (
                            <Image src={remoteImage(businessLogo)} style={styles.businessLogo} />
                        )}
                    </View>
                    <View style={styles.coverContent}>
                        {memorial.image && (
                            <Image src={remoteImage(memorial.image)} style={styles.coverImage} />
                        )}
                        <Text style={styles.coverName}>{fullName}</Text>
                        {memorial.birthDate && memorial.deathDate && (
                            <Text style={styles.coverDates}>
                                {`${formatDate(memorial.birthDate)} — ${formatDate(memorial.deathDate)}`}
                            </Text>
                        )}
                    </View>
                    <View style={styles.coverFooter}>
                        <Image src={FIOH_LOGO} style={styles.coverFooterLogo} />
                    </View>
                </View>
            </Page>

            {/* Content Page(s) */}
            {hasContent && (
                <Page size="A4" style={styles.page}>
                    {serviceDetails.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.heading}>Service Details</Text>
                            {serviceDetails.map((detail) => (
                                <Text key={detail.label} style={styles.body}>
                                    <Text style={styles.label}>{`${detail.label}: `}</Text>
                                    {detail.value}
                                </Text>
                            ))}
                        </View>
                    )}

                    {memorial.obituary && (
                        <View style={styles.section}>
                            <Text style={styles.heading}>Obituary</Text>
                            <Text style={styles.body}>{memorial.obituary}</Text>
                        </View>
                    )}

                    {timeline.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.heading}>Timeline</Text>
                            {timeline.map((entry) => (
                                <View key={entry.id} style={styles.entry} wrap={false}>
                                    <Text style={styles.entryTitle}>
                                        <Text style={styles.label}>
                                            {formatTimelineDate(entry)}
                                        </Text>
                                        {entry.title ? `  ${entry.title}` : ""}
                                    </Text>
                                    {entry.description && (
                                        <Text style={styles.body}>{entry.description}</Text>
                                    )}
                                    {mediaImage(entry.media, videoPosters) && (
                                        <Image
                                            src={mediaImage(entry.media, videoPosters) || ""}
                                            style={styles.entryImage}
                                        />
                                    )}
                                </View>
                            ))}
                        </View>
                    )}

                    {gallery.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.heading}>Gallery</Text>
                            {gallery.map((section) => (
                                <View key={section.id} style={styles.entry}>
                                    {section.title && (
                                        <Text style={styles.entryTitle}>{section.title}</Text>
                                    )}
                                    <View style={styles.galleryGrid}>
                                        {section.images.map((m) => (
                                            <Image
                                                key={m.id}
                                                src={m.src || ""}
                                                style={styles.galleryImage}
                                            />
                                        ))}
                                    </View>
                                </View>
                            ))}
                        </View>
                    )}

                    {tributes.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.heading}>Tributes</Text>
                            {tributes.map((tribute) => (
                                <View key={tribute.id} style={styles.entry} wrap={false}>
                                    <Text style={styles.entryTitle}>
                                        {tribute.name}
                                        <Text style={styles.entryDate}>{`   ${formatDate(tribute.timestamp)}`}</Text>
                                    </Text>
                                    {tribute.message && (
                                        <Text style={styles.body}>{tribute.message}</Text>
                                    )}
                                    {mediaImage(tribute.media, videoPosters) && (
                                        <Image
                                            src={mediaImage(tribute.media, videoPosters) || ""}
                                            style={styles.entryImage}
                                        />
                                    )}
                                </View>
                            ))}
                        </View>
                    )}

                    <View style={styles.footer} fixed>
                        <Image src={FIOH_LOGO} style={styles.footerLogo} />
                    </View>
                </Page>
            )}
        </Document>
    );
};

const styles = StyleSheet.create({
    page: {
        padding: 40,
        paddingBottom: 70,
        fontSize: 11,
        fontFamily: "Helvetica",
        color: "#222",
        backgroundColor: "#fff",
    },
    cover: {
        flexGrow: 1,
        justifyContent: "space-between",
        padding: 26,
        border: "1px solid #c8c8c8",
        borderRadius: 8,
        marginBottom: -30,
    },
    coverHeader: {
        alignItems: "center",
        marginBottom: 20,
    },
    businessLogo: {
        height: 40,
        width: "auto",
        objectFit: "contain",
    },
    coverContent: {
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
    },
    coverImage: {
        height: 270,
        borderRadius: 16,
        marginTop: -32,
        marginBottom: 20,
    },
    coverName: {
        fontFamily: "YesevaOne",
        fontSize: 28,
        marginTop: 10,
        marginBottom: 5,
    },
    coverDates: {
        fontFamily: "YesevaOne",
        fontSize: 16,
        marginVertical: 5,
        color: "#555",
    },
    coverFooter: {
        position: "absolute",
        bottom: 24,
        left: 0,
        right: 0,
        alignItems: "center",
    },
    coverFooterLogo: {
        height: 35,
    },
    section: {
        marginBottom: 22,
    },
    heading: {
        fontFamily: "YesevaOne",
        fontSize: 18,
        marginBottom: 10,
        paddingBottom: 4,
        borderBottom: "1px solid #c8c8c8",
    },
    label: {
        fontFamily: "Helvetica-Bold",
    },
    body: {
        lineHeight: 1.45,
        marginBottom: 4,
    },
    entry: {
        marginBottom: 12,
    },
    entryTitle: {
        fontFamily: "Helvetica-Bold",
        fontSize: 12,
        marginBottom: 3,
    },
    entryDate: {
        fontFamily: "Helvetica",
        fontSize: 9,
        color: "#777",
    },
    entryImage: {
        height: 180,
        width: 240,
        objectFit: "cover",
        borderRadius: 6,
        marginTop: 6,
    },
    galleryGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    galleryImage: {
        width: 165,
        height: 124,
        objectFit: "cover",
        borderRadius: 6,
    },
    footer: {
        position: "absolute",
        bottom: 24,
        left: 40,
        right: 40,
        alignItems: "center",
    },
    footerLogo: {
        height: 30,
    },
});

export default MemorialDocument;
